import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Friendly banner nudging the patient to renew when they only have 1
 * consultation left (or have already exhausted the plan). Silently hides
 * for unlimited plans and users with plenty of remaining calls.
 *
 * Placed at the top of PatientDashboard so it catches attention before
 * they attempt another call.
 */
export const RenewalNudge = () => {
  const { t, language } = useLanguage();
  const [sub, setSub] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/subscriptions/my-subscription`, { withCredentials: true })
      .then((r) => setSub(r.data))
      .catch(() => setSub(null));
  }, []);

  if (!sub || sub.unlimited) return null;
  const remaining = sub.consultations_remaining;
  if (typeof remaining !== 'number' || remaining > 1) return null;
  if (!sub.active) return null;

  const exhausted = remaining <= 0;
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div
      dir={dir}
      data-testid="renewal-nudge-banner"
      className={`mb-6 rounded-2xl border-2 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm ${
        exhausted
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
            exhausted ? 'bg-red-500' : 'bg-amber-500'
          } text-white`}
        >
          <AlertTriangle size={22} />
        </div>
        <div>
          <p className="font-bold text-[#2c3e50]" data-testid="renewal-nudge-title">
            {exhausted
              ? t('استنفدت باقتك', 'You have used every consultation in your plan', 'Consultations épuisées')
              : t('استشارة واحدة متبقية فقط', 'Only 1 consultation left', 'Une seule consultation restante')}
          </p>
          <p className="text-sm text-gray-700 mt-0.5" data-testid="renewal-nudge-subtitle">
            {exhausted
              ? t('جدّد اشتراكك للاستمرار في الحصول على الاستشارات الفورية.',
                  "Renew now so you can keep booking instant consultations whenever you need them.",
                  'Renouvelez pour continuer les consultations instantanées.')
              : t('جدّد الآن لتجنّب الانقطاع في المرة القادمة.',
                  'Renew now so you are not blocked mid-need next time.',
                  'Renouvelez pour éviter tout blocage.')}
          </p>
        </div>
      </div>
      <Link to="/subscriptions" className="shrink-0">
        <Button
          className={`h-11 px-6 rounded-xl text-white ${
            exhausted ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
          }`}
          data-testid="renewal-nudge-cta"
        >
          {t('جدد الآن', 'Renew now', 'Renouveler')}
          <ArrowRight size={16} className="ms-2" />
        </Button>
      </Link>
    </div>
  );
};

export default RenewalNudge;
